import { useEffect, useState } from "react";
import { 
  setComparisonDemographic, 
  setComparisonRegion, 
  setComparisonTopic, 
  setComparisonMetric 
} from "../context/comparisonSlice";
import { axiosInstance } from "lib/axios";
import { useDispatch, useSelector } from "react-redux";
import { analyticsAPI } from "services/apis.service";
import { setAnalyticsTimeframe, setPublisherId } from "../context";

export function useComparisonAnalytics() {
  const [comparisonByTopic, setComparisonByTopic] = useState(null);
  const [engagementLeaderboard, setEngagementLeaderboard] = useState(null);
  const [visitorShare, setVisitorShare] = useState(null);
  const [bookmarksShare, setBookmarksShare] = useState(null);
  const [publisherShare, setPublisherShare] = useState([]);
  const [category, setCategory] = useState(-1);

  const [isLoadingComparisonByTopic, setIsLoadingComparisonByTopic] = useState(true);
  const [isLoadingEngagementLeaderboard, setIsLoadingEngagementLeaderboard] = useState(true);
  const [isLoadingVisitorShare, setIsLoadingVisitorShare] = useState(true);
  const [isLoadingBookmarksShare, setIsLoadingBookmarksShare] = useState(true);
  const [isLoadingPublisherShare, setIsLoadingPublisherShare] = useState(true);

  const isForbidden = (status) => Number(status) === 403;
 
  const dispatch = useDispatch();
  const { demographic, region, topic, metric } = useSelector((state) => state.comparison);
  const { analyticsTimeframe, publisherId, publisherName, publisherRole, roles } = useSelector((state) => state.publisher);

  const getComparisonByTopic = async () => {
    try {
      setIsLoadingComparisonByTopic(true);
      const response = await axiosInstance.post(analyticsAPI.getPublisherComparisonByTopic, {
        "paginate": false,
        "pageIndex": 0,
        "pageSize": 10,
        "dataFrequencyId": analyticsTimeframe,
        "topicId": topic,
        "categoryId": category,
        "publisherId": publisherId,
      });
      setComparisonByTopic(response.data);
    } catch (error) {
      if (isForbidden(error?.response?.status)) {
        setComparisonByTopic(null);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoadingComparisonByTopic(false);
    }
  };

  const getEngagementLeaderboard = async () => {
    try {
      setIsLoadingEngagementLeaderboard(true);
      const response = await axiosInstance.post(analyticsAPI.getPublisherComparisonEngagementLeaderboard, {
        "dataFrequencyId": analyticsTimeframe,
        // "demographic": demographic,
        // "region": region,
        "publisherId": publisherId,
        "metricId": metric,
        "pageNumber": 1,
        "pageSize": 10
      });
      setEngagementLeaderboard(response.data);
    } catch (error) {
      if (isForbidden(error?.response?.status)) {
        setEngagementLeaderboard(null);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoadingEngagementLeaderboard(false);
    }
  };

  const getVisitorShare = async () => {
    try {
      setIsLoadingVisitorShare(true);
      const response = await axiosInstance.post(analyticsAPI.getPublisherComparisonVisitorShare, {
        "dataFrequencyId": analyticsTimeframe,
        // "demographic": demographic,
        // "region": region,
        "publisherId": publisherId,
        "topCount": 10
      });
      setVisitorShare(response.data);
    } catch (error) {
      if (isForbidden(error?.response?.status)) {
        setVisitorShare(null);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoadingVisitorShare(false);
    }
  };

  const getBookmarksShare = async () => {
    try {
      setIsLoadingBookmarksShare(true);
      const response = await axiosInstance.post(analyticsAPI.getPublisherComparisonBookmarksShare, {
        "dataFrequencyId": analyticsTimeframe,
        // "demographic": demographic,
        // "region": region,
        "publisherId": publisherId,
        "topCount": 10
      });
      setBookmarksShare(response.data);
    } catch (error) {
      if (isForbidden(error?.response?.status)) {
        setBookmarksShare(null);
      } else {
        console.error(error);
      }
    } finally {
      setIsLoadingBookmarksShare(false);
    }
  };

  const getPublisherShare = async () => {
    try {
      setIsLoadingPublisherShare(true);
      const response = await axiosInstance.post(analyticsAPI.getPublisherShare, {
          dataFrequencyId: analyticsTimeframe,
          publisherId: publisherId
        });
      setPublisherShare(response.data);
    } catch (error) {
        if (isForbidden(error?.response?.status)) {
          setPublisherShare([]);
        } else {
          console.error(error);
        }
    } finally {
      setIsLoadingPublisherShare(false);
    }
  };

  useEffect(() => {
    if (!publisherId) return;
    getComparisonByTopic();
  }, [analyticsTimeframe, topic, category, publisherId]);

  useEffect(() => {
    if (!publisherId) return;
    getEngagementLeaderboard();
  }, [analyticsTimeframe, metric, publisherId]);

  useEffect(() => {
    if (!publisherId) return;
    getVisitorShare();
    getBookmarksShare();
    // getPublisherShare();
  }, [analyticsTimeframe, publisherId]);

  return {
    timeframe: analyticsTimeframe,
    demographic,
    region,
    topic,
    metric,
    category,
    publisherId,
    publisherName,
    publisherRole,
    roles,
    publisherShare,
    setTimeframe: (value) => {
      dispatch(setAnalyticsTimeframe(value));
    },
    setDemographic: (value) => {
      dispatch(setComparisonDemographic(value));
    },
    setRegion: (value) => {
      dispatch(setComparisonRegion(value));
    },
    setTopic: (value) => {
      dispatch(setComparisonTopic(value));
    },
    setMetric: (value) => {
      dispatch(setComparisonMetric(value));
    },
    setCategory,
    setPublisherId: (id) => {
      dispatch(setPublisherId(id));
    },
    comparisonByTopic,
    engagementLeaderboard,
    visitorShare,
    bookmarksShare,
    isLoadingComparisonByTopic,
    isLoadingEngagementLeaderboard,
    isLoadingVisitorShare,
    isLoadingBookmarksShare,
    isLoadingPublisherShare,
  };
}

export default useComparisonAnalytics;